import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { fetchUserData, updateUserStats, fetchWordStats, updateWordStatsDB, saveGameSession, fetchGameSessions, fetchBestScore } from '../services/supabaseService';
import { classifyWords, generateMixedPractice } from '../services/gameLogic';
import { playSound } from '../utils/soundManager';
import { evaluateMissedDays, normalizeDate, shouldResetWorkout, calculateMilestoneReward } from '../services/streakService';
import { awardWorkoutCoins } from '../services/coinService';
import { fetchAllWords, fetchVisualizationWords, fetchConnotationWords, fetchSynonymRelationships, fetchToneClashRelationships } from '../services/wordService';


export const useGameState = ({ user, setUser, step, setStep, muted, setMuted }) => {
  const [gameType, setGameType] = useState(null);
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentWord, setCurrentWord] = useState(null);
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [, setTime] = useState(10); // eslint-disable-line no-unused-vars
  const [feedback, setFeedback] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(0);
  const [speedyCount, setSpeedyCount] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedPair, setSelectedPair] = useState([]);
  const [correctPairIds, setCorrectPairIds] = useState([]);
  const [, setTouchStartX] = useState(null); // eslint-disable-line no-unused-vars
  const [, setTouchEndX] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedSet, setSelectedSet] = useState("mixed");
  const [playMode, setPlayMode] = useState("learn");
  const [isPaused, setIsPaused] = useState(false);
  const [isTrendOpen, setIsTrendOpen] = useState(false);
  const [gameLength, setGameLength] = useState("quick");
  const [hasCountedCompletion, setHasCountedCompletion] = useState(false);
  const [sessionRecorded, setSessionRecorded] = useState(false);
  const [, setDailyGoalMet] = useState(false); // eslint-disable-line no-unused-vars
  const [isGameOver, setIsGameOver] = useState(false);
  const [showLivesOverPopup, setShowLivesOverPopup] = useState(false);
  const [workoutReward, setWorkoutReward] = useState(0);
  const [showMeaningFeedback, setShowMeaningFeedback] = useState(() => {
    try {
      const stored = localStorage.getItem("vocab_show_meaning_feedback");
      return stored !== null ? stored === "true" : true;
    } catch (e) { return true; }
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isShowingMeaning, setIsShowingMeaning] = useState(false);

  const toggleMeaningFeedback = () => {
    setShowMeaningFeedback(prev => {
      const newVal = !prev;
      try { localStorage.setItem("vocab_show_meaning_feedback", String(newVal)); } catch (e) { }
      return newVal;
    });
  };

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
    setIsPaused(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
    if (!feedback || feedback === "correct") {
      setIsPaused(false);
    }
  };

  const wordsRef = useRef([]);
  useEffect(() => { wordsRef.current = words; }, [words]);
  const [streakFeedback, setStreakFeedback] = useState(null);
  const [dbHistory, setDbHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [dbBestScore, setDbBestScore] = useState(0);
  const [scoreHistoryMap, setScoreHistoryMap] = useState(() => {
    try {
      const stored = localStorage.getItem("vocab_scores_map");
      if (stored) return JSON.parse(stored);
    } catch (e) { }
    return {};
  });

  // Tracking recently played synonym pairs to prevent immediate repetition
  const recentTwins = useRef([]);

  // ---- DB helpers ----
  const updateWordStats = async (wordInput, isCorrect) => {
    // wordInput can be a single string or an array of strings
    const wordsToUpdate = Array.isArray(wordInput) ? wordInput : [wordInput];
    
    try {
      if (user) {
        await Promise.all(
          wordsToUpdate.map(wordText => updateWordStatsDB(user.id, wordText, isCorrect))
        );
      }
    } catch (error) {
      console.error(`[DB ERROR] Failed to update stats:`, error.message);
    }
  };

  useEffect(() => {
    if (user && user.id && gameType && gameLength) {
      fetchBestScore(user.id, gameType, gameLength).then(res => {
        if (!res.error) setDbBestScore(res.bestScore);
      }).catch(err => console.error("Error fetching best score:", err));
    }
  }, [user, gameType, gameLength]);

  const saveSessionToSupabase = async (sessionData) => {
    try {
      if (!user || !user.id) return;


      const { error } = await saveGameSession(user.id, sessionData);
      if (error) throw error;
    } catch (error) {
      console.error("❌ Error saving session:", error.message);
    }
  };

  const [correctOptionId, setCorrectOptionId] = useState(null);

  // ---- Option generators ----
  const generateOptions = async (word, allWords) => {
    const fullPool = await fetchAllWords();
    // Use the full pool for distractors, ensuring they are not the target word
    const distractors = fullPool
      .filter(w => w.word !== word.word)
      .sort(() => 0.5 - Math.random())
      .slice(0, 2);
    setCorrectOptionId(word.id);
    setOptions([word, ...distractors].sort(() => 0.5 - Math.random()));
  };

  const [visualizationQuestions, setVisualizationQuestions] = useState([]);
  const preloadedIndices = useRef(new Set());

  const preloadImages = (urls) => {
    if (!urls || urls.length === 0) return Promise.resolve();
    return Promise.all(
      urls.map((url) => {
        return new Promise((resolve) => {
          if (!url) return resolve();
          const img = new Image();
          
          let timeoutId = setTimeout(() => {
            resolve();
          }, 3000);

          img.onload = () => {
            if (img.decode) {
              img.decode().then(() => {
                clearTimeout(timeoutId);
                resolve();
              }).catch(() => {
                clearTimeout(timeoutId);
                resolve();
              });
            } else {
              clearTimeout(timeoutId);
              resolve();
            }
          };
          
          img.onerror = () => {
            clearTimeout(timeoutId);
            resolve();
          };
          
          img.src = url;
        });
      })
    );
  };

  const generateImageOptions = async (correctWord, pool) => {
    const visualWords = pool || await fetchVisualizationWords();
    const incorrectWords = visualWords
      .filter(w => w.word !== correctWord.word)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3)
      .map(w => w.image_url || `/images/${w.word.toLowerCase().trim()}.png`);
    
    return [correctWord.image_url || `/images/${correctWord.word.toLowerCase().trim()}.png`, ...incorrectWords].sort(() => 0.5 - Math.random());
  };

  const generateSynonymOptions = async (rel) => {
    // In Twins mode, the "word" passed is actually a relationship object
    const allWords = await fetchAllWords();
    const allRels = await fetchSynonymRelationships();

    const word1 = rel.wordObj;
    const synonym = rel.relatedWordObj;
    
    // Build a set of forbidden IDs (the pair itself + all direct synonyms)
    // to prevent "multiple right answers" ambiguity.
    const forbiddenIds = new Set([word1.id, synonym.id]);
    
    allRels.forEach(r => {
      if (r.word_id === word1.id) forbiddenIds.add(r.related_word_id);
      if (r.related_word_id === word1.id) forbiddenIds.add(r.word_id);
      if (r.word_id === synonym.id) forbiddenIds.add(r.related_word_id);
      if (r.related_word_id === synonym.id) forbiddenIds.add(r.word_id);
    });

    setCorrectPairIds([word1.id, synonym.id]);

    const distractors = allWords
      .filter(w => !forbiddenIds.has(w.id))
      .sort(() => 0.5 - Math.random())
      .slice(0, 2);

    setOptions([word1, synonym, ...distractors].sort(() => 0.5 - Math.random()));
  };

  const generateToneClashOptions = (anchorWord) => {
    // 1. Correct Option
    const correctOption = {
      id: "tone_clash_correct",
      word: anchorWord.clashWord,
      meaning: "The opposite of " + anchorWord.word
    };

    // 2. Precomputed Distractors
    const distractors = (anchorWord.clashDistractors || []).map((d, i) => ({
      id: `tone_clash_distractor_${i}`,
      word: d,
      meaning: "Incorrect Option"
    }));

    // 3. Assemble and Shuffle
    const finalSet = [correctOption, ...distractors].sort(() => 0.5 - Math.random());

    // 4. Update state
    setCorrectOptionId("tone_clash_correct");
    setOptions(finalSet);
  };

  // ---- Core game flow ----
  const finishGameSession = async () => {
    if (hasCountedCompletion) return;
    setHasCountedCompletion(true);

    const today = normalizeDate();
    const questionReached = currentIndex + 1;
    const threshold = gameLength === "quick" ? 7 : 12;
    const isValidGame = questionReached >= threshold;

    const isFirstSuccessToday = (user.lastPlayedDate !== today || (user.successfulGamesCount || 0) === 0) && isValidGame;

    const newDailyCount = (user.lastPlayedDate === today) ? (user.gamesCompletedToday + 1) : 1;
    const newSuccessfulCount = (user.lastPlayedDate === today) ? ((user.successfulGamesCount || 0) + (isValidGame ? 1 : 0)) : (isValidGame ? 1 : 0);

    setUser(prev => ({
      ...prev,
      gamesCompletedToday: newDailyCount,
      lastPlayedDate: today,
      successfulGamesCount: newSuccessfulCount
    }));

    const reward = awardWorkoutCoins({ ...user, successfulGamesCount: newSuccessfulCount }, today);
    setWorkoutReward(reward);

    setStep("result");
    setIsTrendOpen(false);

    try {
      const { data: userData, error: fetchError } = await fetchUserData(user.id);
      if (fetchError) throw fetchError;

      const prevStreak = userData.streak || 0;
      const lastActive = userData.last_active_date;
      const lastFreezeEarnedDate = userData.last_freeze_earned_date;
      const evaluation = evaluateMissedDays(userData, today);

      let newStreak = evaluation.updatedStreak;
      let newFreezes = evaluation.updatedFreezes;
      let didUseFreeze = evaluation.freezeUsed;
      let didEarnFreeze = false;
      let newLastFreezeEarnedDate = lastFreezeEarnedDate;

      if (isFirstSuccessToday) {
        if (newStreak > 0 || prevStreak === 0) {
          if (lastActive === today) {
            if (prevStreak === user.currentStreak) {
              newStreak = prevStreak + 1;
            }
          } else {
            newStreak++;
          }
        } else {
          newStreak = 1;
        }
      }

      const earnedFreeze = calculateMilestoneReward(prevStreak, newStreak, newFreezes);
      if (isFirstSuccessToday && earnedFreeze > 0 && lastFreezeEarnedDate !== today) {
        newFreezes += earnedFreeze;
        newLastFreezeEarnedDate = today;
        didEarnFreeze = true;
      }

      const dbUpdates = {
        streak: newStreak,
        daily_games_count: newDailyCount,
        successful_games_count: newSuccessfulCount,
        streak_freezes: newFreezes,
        last_freeze_earned_date: newLastFreezeEarnedDate,
        last_activity_at: new Date().toISOString(),
        coins: (user.coins || 0) + reward,
        last_coin_reward_date: reward > 0 ? today : (user.lastCoinRewardDate || null)
      };

      if (isValidGame) {
        dbUpdates.last_active_date = today;
      }

      const { error: updateError } = await updateUserStats(user.id, dbUpdates);
      if (updateError) throw updateError;

      setUser(prev => ({
        ...prev,
        currentStreak: newStreak,
        streakFreezes: newFreezes,
        lastFreezeEarnedDate: newLastFreezeEarnedDate,
        coins: (user.coins || 0) + reward,
        lastCoinRewardDate: reward > 0 ? today : (user.lastCoinRewardDate || null)
      }));

      const finalWords = wordsRef.current;
      const correctCount = finalWords.filter(w => w.status === "correct").length;
      const wrongCount = finalWords.filter(w => w.status === "wrong").length;
      const attempted = correctCount + wrongCount;
      const accuracy = attempted ? Math.round((correctCount / attempted) * 100) : 0;
      const sessionData = { gameType, mode: playMode, score, accuracy, correct: correctCount, wrong: wrongCount, speedy: speedyCount };

      setScoreHistoryMap(prev => {
        const sessionKey = `${gameType}_${gameLength}`;
        const sessionDataObj = prev[sessionKey] || { history: [], best: 0 };
        const history = Array.isArray(sessionDataObj) ? sessionDataObj : (sessionDataObj.history || []);
        const best = Array.isArray(sessionDataObj) ? Math.max(...history.map(h => h.score), 0) : (sessionDataObj.best || 0);
        const newHistory = [...history, { userId: user.id, ...sessionData, timestamp: Date.now() }].slice(-5);
        const newMap = { ...prev, [sessionKey]: { history: newHistory, best: Math.max(best, score) } };
        try { localStorage.setItem("vocab_scores_map", JSON.stringify(newMap)); } catch (e) { }
        return newMap;
      });

      await saveSessionToSupabase({ ...sessionData, mode: gameLength });
      setSessionRecorded(true);

      setIsHistoryLoading(true);
      const { data: historyData } = await fetchGameSessions(user.id, gameType, gameLength);
      if (historyData) setDbHistory(historyData);
      setIsHistoryLoading(false);

      if (isFirstSuccessToday) {
        let msg = "Nice consistency. Keep it going!";
        let title = `+1 Streak 🔥`;
        let subtitle = `You're on a ${newStreak} day streak`;
        let type = "streak";

        if (didUseFreeze) {
          type = "freeze_used";
          title = "Streak Saved 🛡️";
          subtitle = `Your ${newStreak} day streak is safe`;
          msg = `We used 1 streak freeze. ${newFreezes} remaining.`;
        } else if (didEarnFreeze && newStreak > prevStreak) {
          type = "streak_earned";
          title = "Milestone! 🎁";
          subtitle = `${newStreak} Days Completed`;
          msg = `+1 streak freeze earned for your progress!`;
        } else if (newStreak > prevStreak) {
          if (newStreak === 1) msg = "Great start. You've begun a strong habit.";
          else if (newStreak === 3) msg = "You're building consistency now.";
          else if (newStreak === 7) msg = "This is becoming a real habit.";
          else if (newStreak === 14) msg = "Strong habit forming 💪";
          else if (newStreak === 30) msg = "Elite consistency achieved 🔥";
        }

        playSound("streak");
        setStreakFeedback({ type, title, subtitle, message: msg, streak: newStreak });
      } else {
        if (lives <= 0) playSound("fail");
      }
    } catch (e) {
      console.error("[ERROR] finishGameSession:", e.message);
      alert("Failed to save your progress. Please check your internet connection.");
    }
  };

  const handleGameEnd = (currentLives = lives) => {
    const questionReached = currentIndex + 1;
    const isFinalQuestion = questionReached >= words.length;

    if (currentLives <= 0 && !isFinalQuestion) {
      setShowLivesOverPopup(true);
      return;
    }

    setIsGameOver(true);
    setCurrentWord(null);
  };

  const confirmLivesOver = () => {
    setShowLivesOverPopup(false);
    setIsGameOver(true);
    setCurrentWord(null);
  };

  useEffect(() => {
    if (isGameOver && !sessionRecorded && wordsRef.current.length > 0) {
      finishGameSession();
    }
  }, [isGameOver, sessionRecorded]);

  const nextWord = async () => {
    if (!currentWord || isGameOver) return;
    if (lives <= 0) { handleGameEnd(lives); return; }
    if (currentIndex + 1 >= words.length || lives <= 0) { setIsGameOver(true); return; }

    const next = currentIndex + 1;
    setCurrentIndex(next);
    setCurrentWord(words[next]);
    setFeedback(null);
    setSelectedOption(null);
    setSelectedPair([]);
    setQuestionStartTime(Date.now());

    if (gameType === "meaning") await generateOptions(words[next], words);
    else if (gameType === "visualization") {
      const q = visualizationQuestions[next];
      if (q) {
        setCorrectOptionId(q.correctWordId);
        setOptions(q.options);
        // Preload next in buffer (current index + 2)
        const preloadIdx = next + 2;
        if (visualizationQuestions[preloadIdx] && !preloadedIndices.current.has(preloadIdx)) {
          preloadedIndices.current.add(preloadIdx);
          preloadImages(visualizationQuestions[preloadIdx].options);
        }
      }
    }
    else if (gameType === "twins") await generateSynonymOptions(words[next]);
    else if (gameType === "tone_clash") generateToneClashOptions(words[next]);
    else setSelectedOption(null);
  };

  const goHome = () => {
    setStep("home"); setGameType(null); setSelectedGroup(null);
    setWords([]); setCurrentIndex(0); setCurrentWord(null);
    setScore(0); setElapsedTime(0); setSpeedyCount(0);
    setLives(gameLength === "quick" ? 3 : 5); setTime(10);
    setFeedback(null); setSelectedOption(null); setSelectedPair([]); setOptions([]);
    setIsTrendOpen(false); setIsShowingMeaning(false);
    setWorkoutReward(0);
    setVisualizationQuestions([]);
    preloadedIndices.current = new Set();
  };

  const startGame = async (selectedWords, typeOfGameParam, currentPlayMode, forceGameLength) => {
    if (!selectedWords || selectedWords.length === 0) return;

    const activePlayMode = currentPlayMode || playMode;
    const activeGameType = typeOfGameParam || gameType;
    const activeGameLength = forceGameLength || (selectedWords.length > 12 ? "standard" : "quick");

    setPlayMode(activePlayMode);
    setGameType(activeGameType);
    setGameLength(activeGameLength);

    setWords([]); setFeedback(null); setSelectedOption(null); setIsGameOver(false); setShowLivesOverPopup(false); setIsShowingMeaning(false);
    setDbHistory([]);

    const today = normalizeDate();
    let currentCount = user.gamesCompletedToday;
    if (shouldResetWorkout(user.lastPlayedDate, today) && user.lastPlayedDate !== null) currentCount = 0;
    const dailyLimit = 500;
    if (currentCount >= dailyLimit) {
      alert("Daily limit reached. Take a break and come back tomorrow!");
      return;
    }

    const shuffled = [...selectedWords].sort(() => 0.5 - Math.random()).map(w => ({ ...w, status: null }));
    setWords(shuffled); setCurrentIndex(0); setCurrentWord(shuffled[0]);
    setScore(0); setLives(activeGameLength === "quick" ? 3 : 5);
    setElapsedTime(0); setSpeedyCount(0); setQuestionStartTime(Date.now());
    setHasCountedCompletion(false); setIsPaused(false); setIsSettingsOpen(false); setSessionRecorded(false); setDailyGoalMet(false);
    setWorkoutReward(0);

    updateUserStats(user.id, { last_activity_at: new Date().toISOString() }).catch(() => { });

    if (activeGameType === "meaning") await generateOptions(shuffled[0], shuffled);
    else if (activeGameType === "visualization") {
      // For visualization, we pre-generate all question options first
      const visualWords = await fetchVisualizationWords();
      const sessionQuestions = await Promise.all(shuffled.map(async (word) => {
        const options = await generateImageOptions(word, visualWords);
        return {
          word: word,
          correctWordId: word.id,
          options: options
        };
      }));
      setVisualizationQuestions(sessionQuestions);
      setCorrectOptionId(sessionQuestions[0].correctWordId);
      setOptions(sessionQuestions[0].options);

      // Startup Preload: Current + Next 2
      preloadedIndices.current = new Set([0, 1, 2]);
      await Promise.all([
        preloadImages(sessionQuestions[0].options),
        sessionQuestions[1] ? preloadImages(sessionQuestions[1].options) : Promise.resolve(),
        sessionQuestions[2] ? preloadImages(sessionQuestions[2].options) : Promise.resolve()
      ]);
    }
    else if (activeGameType === "twins") await generateSynonymOptions(shuffled[0]);
    else if (activeGameType === "tone_clash") generateToneClashOptions(shuffled[0]);
    
    // Gated Render: Only switch to game/preview screen AFTER all async setup (including preloads) is done.
    if (activePlayMode === "learn") setStep("preview"); else setStep("game");

    setIsStarting(false);
  };

  const handleConnotation = async (ans) => {
    if (!currentWord || feedback || isGameOver) return;
    const mappedConnotation = currentWord.connotation === "strongly_positive" ? "positive" : "negative";
    const isCorrect = ans === mappedConnotation;
    updateWordStats(currentWord.word, isCorrect);

    setWords(prev => {
      const updated = [...prev];
      updated[currentIndex] = { ...updated[currentIndex], status: isCorrect ? "correct" : "wrong" };
      return updated;
    });

    if (isCorrect) {
      const timeTaken = (Date.now() - questionStartTime) / 1000;
      let points = 50;
      if (timeTaken <= 3) { points += 30; setSpeedyCount(c => c + 1); } else if (timeTaken <= 5) points += 15;
      if (playMode === "quick") points += lives * 10;
      setScore(s => s + points); setFeedback("correct"); playSound("correct");
    } else {
      setFeedback("wrong"); playSound("wrong");
      const nextLives = lives - 1; setLives(Math.max(0, nextLives));
      if (showMeaningFeedback) {
        setIsPaused(true);
        setIsShowingMeaning(true);
      } else {
        if (nextLives <= 0) { handleGameEnd(nextLives); return; }
      }
    }
    if (isCorrect || (!isCorrect && !showMeaningFeedback)) {
      setTimeout(() => { if (!isGameOver) { setFeedback(null); nextWord(); } }, isCorrect ? 400 : 600);
    }
  };

  const handleOptionClick = async (opt) => {
    if (feedback || isGameOver) return;

    if (gameType === "twins") {
      if (selectedPair.find(s => s.id === opt.id)) {
        setSelectedPair(prev => prev.filter(s => s.id !== opt.id));
        return;
      }

      const newPair = [...selectedPair, opt];
      setSelectedPair(newPair);

      if (newPair.length === 2) {
        const isCorrect = correctPairIds.includes(newPair[0].id) && correctPairIds.includes(newPair[1].id);
        // Always update the true synonym pair, not the selected distractors
        updateWordStats([currentWord.wordObj.word, currentWord.relatedWordObj.word], isCorrect);

        setWords(prev => {
          const updated = [...prev];
          updated[currentIndex] = { ...updated[currentIndex], status: isCorrect ? "correct" : "wrong" };
          return updated;
        });

        if (isCorrect) {
          const timeTaken = (Date.now() - questionStartTime) / 1000;
          let points = 50;
          if (timeTaken <= 3) { points += 30; setSpeedyCount(c => c + 1); } else if (timeTaken <= 5) points += 15;
          if (playMode === "quick") points += lives * 10;
          setScore(s => s + points); setFeedback("correct"); playSound("correct");
          setTimeout(() => { if (!isGameOver) nextWord(); }, 600);
        } else {
          setFeedback("wrong"); playSound("wrong");
          const nextLives = lives - 1; setLives(Math.max(0, nextLives));
          if (showMeaningFeedback) {
            setIsPaused(true);
            setIsShowingMeaning(true);
          } else {
            if (nextLives <= 0) { handleGameEnd(nextLives); return; }
            setTimeout(() => { if (!isGameOver) nextWord(); }, 600);
          }
        }
      }
    } else {
      const isCorrect = opt.id === correctOptionId;
      updateWordStats(currentWord.word, isCorrect);

      setWords(prev => {
        const updated = [...prev];
        updated[currentIndex] = { ...updated[currentIndex], status: isCorrect ? "correct" : "wrong" };
        return updated;
      });
      setSelectedOption(opt);

      if (isCorrect) {
        const timeTaken = (Date.now() - questionStartTime) / 1000;
        let points = 50;
        if (timeTaken <= 3) { points += 30; setSpeedyCount(c => c + 1); } else if (timeTaken <= 5) points += 15;
        if (playMode === "quick") points += lives * 10;
        setScore(s => s + points); setFeedback("correct"); playSound("correct");
        setTimeout(() => { if (!isGameOver) nextWord(); }, 600);
      } else {
        setFeedback("wrong"); playSound("wrong");
        const nextLives = lives - 1; setLives(Math.max(0, nextLives));
        if (showMeaningFeedback) {
          setIsPaused(true);
          setIsShowingMeaning(true);
        } else {
          if (nextLives <= 0) { handleGameEnd(nextLives); return; }
          setTimeout(() => { if (!isGameOver) nextWord(); }, 600);
        }
      }
    }
  };

  const handleVisualization = async (knewIt) => {
    if (feedback) return;
    const isCorrect = knewIt === currentWord.imageUrl;
    updateWordStats(currentWord.word, isCorrect);

    setWords(prev => {
      const updated = [...prev];
      updated[currentIndex] = { ...updated[currentIndex], status: isCorrect ? "correct" : "wrong" };
      return updated;
    });
    setSelectedOption(knewIt);

    if (isCorrect) {
      const timeTaken = (Date.now() - questionStartTime) / 1000;
      let points = 50;
      if (timeTaken <= 3) { points += 30; setSpeedyCount(c => c + 1); } else if (timeTaken <= 5) points += 15;
      if (playMode === "quick") points += lives * 10;
      setScore(s => s + points); setFeedback("correct"); playSound("correct");
    } else {
      setFeedback("wrong"); playSound("wrong");
      const nextLives = lives - 1; setLives(Math.max(0, nextLives));
      if (showMeaningFeedback) {
        setIsPaused(true);
        setIsShowingMeaning(true);
      } else {
        if (nextLives <= 0) { handleGameEnd(nextLives); return; }
      }
    }
    if (isCorrect || (!isCorrect && !showMeaningFeedback)) {
      setTimeout(() => { if (!isGameOver) { setFeedback(null); nextWord(); } }, 600);
    }
  };

  const handleLearnContinue = () => {
    setIsPaused(false); setIsShowingMeaning(false); setFeedback(null); setQuestionStartTime(Date.now()); nextWord();
  };

  useEffect(() => {
    if (step !== "game" || isPaused) return;
    const timer = setInterval(() => setElapsedTime(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [step, isPaused]);

  useEffect(() => {
    const handleKey = (e) => {
      if (step === "game" && gameType === "connotation") {
        if (isGameOver) return;
        if (e.key === "ArrowLeft") handleConnotation("negative");
        if (e.key === "ArrowRight") handleConnotation("positive");
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [step, gameType, currentWord, feedback, isGameOver, words, currentIndex]);

  // Cleaned up redundant visualization effect - handled by startGame and nextWord preloading

  useEffect(() => {
    if (step === "game" && gameType === "connotation") {
      const origHtml = document.documentElement.style.overscrollBehavior;
      const origBody = document.body.style.overscrollBehavior;
      document.documentElement.style.overscrollBehavior = "none";
      document.body.style.overscrollBehavior = "none";
      document.body.style.overflow = "hidden"; document.body.style.position = "fixed";
      document.body.style.width = "100%"; document.body.style.height = "100%";
      return () => {
        document.documentElement.style.overscrollBehavior = origHtml;
        document.body.style.overscrollBehavior = origBody;
        document.body.style.overflow = ""; document.body.style.position = "";
        document.body.style.width = ""; document.body.style.height = "";
      };
    }
  }, [step, gameType]);

  useEffect(() => {
    const handleGlobalEnter = (e) => {
      if (e.key === "Enter" && !e.repeat && playMode === "learn" && isPaused && currentWord) {
        handleLearnContinue();
      }
    };
    window.addEventListener("keydown", handleGlobalEnter);
    return () => window.removeEventListener("keydown", handleGlobalEnter);
  }, [step, isPaused, playMode, currentWord]);

  useEffect(() => {
    if (selectedSet !== "mixed" && selectedSet !== "review") {
      setSelectedSet("mixed");
    }
  }, [gameType, selectedSet]);


  const handleSetupStart = useCallback(async () => {
    if (isStarting) return;
    setIsStarting(true);
    let sessionWords = [];
    let baseWords;
    if (gameType === "twins") {
      const rels = await fetchSynonymRelationships();
      const { data: { user: sbUser } } = await supabase.auth.getUser();

      if (sbUser) {
        const { data: stats } = await fetchWordStats(sbUser.id);
        const statsMap = Object.fromEntries((stats || []).map(s => [s.word.toLowerCase().trim(), s]));

        // Status hierarchy (lowest index = most priority/weakest)
        const statusOrder = ['weak', 'learning_negative', 'learning_positive', 'new', 'strong', 'mastered'];
        
        const getStatus = (wordText) => {
          const s = statsMap[wordText.toLowerCase().trim()];
          if (!s || (s.seen_count || 0) === 0) return 'new';
          if ((s.streak || 0) <= -2) return 'weak';
          if ((s.streak || 0) === -1) return 'learning_negative';
          if ((s.streak || 0) === 1) return 'learning_positive';
          if ((s.streak || 0) >= 4 && (s.seen_count || 0) >= 5) return 'mastered';
          if ((s.streak || 0) >= 2) return 'strong';
          return 'new';
        };

        // Filter out recently played pairs for variety
        const availableRels = rels.filter(r => !recentTwins.current.includes(r.id));
        
        // If we filtered too many (small dataset), use all rels to avoid empty session
        const relsToProcess = availableRels.length >= (gameLength === "quick" ? 10 : 20) ? availableRels : rels;

        // Re-classify using the filtered set
        const finalClassified = { weak: [], learning_negative: [], learning_positive: [], new: [], strong: [], mastered: [] };
        relsToProcess.forEach(r => {
          const s1 = getStatus(r.word.word);
          const s2 = getStatus(r.related_word.word);
          const pairStatus = statusOrder[Math.min(statusOrder.indexOf(s1), statusOrder.indexOf(s2))];
          
          const uiCompatibleRel = {
            ...r,
            word: {
              isTwins: true,
              word1: r.word.word,
              meaning1: r.word.meaning,
              word2: r.related_word.word,
              meaning2: r.related_word.meaning,
              toString: () => `${r.word.word} ↔ ${r.related_word.word}`
            },
            meaning: r.word.meaning,
            wordObj: r.word,
            relatedWordObj: r.related_word
          };
          finalClassified[pairStatus].push(uiCompatibleRel);
        });

        sessionWords = generateMixedPractice(finalClassified, gameLength);
        
        // Update cooldown list with the IDs of the pairs in this session
        const sessionRelIds = sessionWords.map(w => w.id).filter(Boolean);
        recentTwins.current = [...sessionRelIds, ...recentTwins.current].slice(0, 15);
      } else {
        const limit = gameLength === "quick" ? 10 : 20;
        sessionWords = [...rels].sort(() => 0.5 - Math.random()).slice(0, limit);
      }
    } else {
      // Normal Single-Word Games
      if (gameType === "meaning") {
        baseWords = await fetchAllWords();
      } else if (gameType === "connotation") {
        baseWords = await fetchConnotationWords();
      } else if (gameType === "visualization") {
        const visualWords = await fetchVisualizationWords();
        baseWords = visualWords.map(w => ({ 
          ...w, 
          imageUrl: w.image_url || `/images/${w.word.toLowerCase().trim()}.png` 
        }));
      } else if (gameType === "tone_clash") {
        const allWords = await fetchAllWords();
        const clashRels = await fetchToneClashRelationships();
        const validSourceIds = new Set(clashRels.map(r => r.source_word_id));
        baseWords = allWords.filter(w => validSourceIds.has(w.id));
      } else {
        baseWords = await fetchAllWords();
      }
      
      const { data: { user: sbUser } } = await supabase.auth.getUser();
        if (sbUser) {
          const { data: stats } = await fetchWordStats(sbUser.id);
          const classified = classifyWords(baseWords, stats || []);
          if (selectedSet === "review") {
            const weakStats = (stats || []).filter(s => (Number(s.streak) || 0) <= -2);
            const weakWords = weakStats.map(s => baseWords.find(w => w.word.toLowerCase().trim() === s.word.toLowerCase().trim())).filter(Boolean);
            let selection = [...weakWords];
            if (selection.length < 10) {
              const learningStats = (stats || []).filter(s => (Number(s.streak) || 0) === -1);
              const learningWords = learningStats.map(s => baseWords.find(w => w.word.toLowerCase().trim() === s.word.toLowerCase().trim())).filter(Boolean).filter(w => !selection.some(sw => sw.word === w.word));
              selection = [...selection, ...learningWords].slice(0, 10);
            }
            if (selection.length < 10) {
              const used = new Set(selection.map(w => w.word));
              selection = [...selection, ...baseWords.filter(w => !used.has(w)).sort(() => 0.5 - Math.random()).slice(0, 10 - selection.length)];
            }
            sessionWords = selection;
          } else {
            const wordStrings = generateMixedPractice(classified, gameLength);
            sessionWords = wordStrings.map(ws => baseWords.find(w => w.word === ws)).filter(Boolean);
          }
        } else {
          const limit = gameLength === "quick" ? 10 : 20;
          sessionWords = [...baseWords].sort(() => 0.5 - Math.random()).slice(0, limit);
        }
      }

    if (gameType === "tone_clash" && sessionWords.length > 0) {
      const clashRels = await fetchToneClashRelationships();
      const relMap = new Map(clashRels.map(r => [r.source_word_id, r]));
      
      sessionWords = sessionWords.map(w => {
        const rel = relMap.get(w.id);
        if (!rel) return { ...w, clashWord: "N/A" };

        const normalize = (s) => (s || "").toLowerCase().trim();
        const options = [
          normalize(rel.antonym_word),
          normalize(rel.distractor_1),
          normalize(rel.distractor_2),
          normalize(rel.distractor_3)
        ].filter(Boolean);

        // Validation: Must have 4 unique options and no empty strings
        const uniqueOptions = new Set(options);
        const isValid = options.length === 4 && uniqueOptions.size === 4;

        if (!isValid) return { ...w, clashWord: "N/A" };



        return {
          ...w,
          isToneClash: true,
          clashWord: rel.antonym_word,
          clashDistractors: [rel.distractor_1, rel.distractor_2, rel.distractor_3],
          clashMeaning: "Opposite of " + w.word
        };
      }).filter(w => w.clashWord !== "N/A");
    }

    if (sessionWords.length === 0) {
      console.error('[CRITICAL] handleSetupStart - sessionWords is empty!');
      alert("No words available for this selection!");
      setIsStarting(false);
      return;
    }
    sessionWords = Array.from(new Map(sessionWords.map(w => [w.word, w])).values());
    startGame(sessionWords, gameType, playMode, gameLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType, gameLength, selectedSet, user, playMode, isStarting]);

  // Review + Mixed practice shortcuts
  const handleStartReview = async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      const { data: { user: sbUser } } = await supabase.auth.getUser();
      if (!sbUser) return;
      
      const { data: stats } = await fetchWordStats(sbUser.id);
      const allWords = await fetchAllWords();
      const wordMap = new Map(allWords.map(w => [w.word.toLowerCase().trim(), w]));

      const weakStats = (stats || []).filter(s => (Number(s.streak) || 0) <= -2);
      const weakWords = weakStats.map(s => wordMap.get(s.word.toLowerCase().trim())).filter(Boolean);
      
      let finalSelection = [...weakWords];
      if (finalSelection.length < 10) {
        const learningWords = (stats || []).filter(s => (Number(s.streak) || 0) === -1)
          .map(s => wordMap.get(s.word.toLowerCase().trim()))
          .filter(Boolean)
          .filter(w => !finalSelection.some(fw => fw.word === w.word));
        finalSelection = [...finalSelection, ...learningWords.slice(0, 10 - finalSelection.length)];
      }
      if (finalSelection.length < 10) {
        const usedWords = new Set(finalSelection.map(w => w.word));
        finalSelection = [...finalSelection, ...allWords.filter(w => !usedWords.has(w.word)).sort(() => 0.5 - Math.random()).slice(0, 10 - finalSelection.length)];
      }
      startGame(Array.from(new Map(finalSelection.map(w => [w.word, w])).values()), "meaning", "quick", "quick");
    } catch (err) {
      console.error("Error starting review:", err);
      setIsStarting(false);
    }
  };

  const handleStartMixedPractice = async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      const { data: { user: sbUser } } = await supabase.auth.getUser();
      if (!sbUser) return;
      
      const { data: stats } = await fetchWordStats(sbUser.id);
      const allWords = await fetchAllWords();
      const wordMap = new Map(allWords.map(w => [w.word.toLowerCase().trim(), w]));

      const weakWords = (stats || []).filter(s => (s.streak || 0) <= -2).map(s => wordMap.get(s.word.toLowerCase().trim())).filter(Boolean);
      const learningWords = (stats || []).filter(s => (s.streak || 0) === -1 || (s.streak || 0) === 1).map(s => wordMap.get(s.word.toLowerCase().trim())).filter(Boolean);
      
      let selectedWords = [...weakWords, ...learningWords].sort(() => 0.5 - Math.random()).slice(0, 10);
      if (selectedWords.length < 10) {
        const usedWords = new Set(selectedWords.map(w => w.word));
        selectedWords = [...selectedWords, ...allWords.filter(w => !usedWords.has(w.word)).sort(() => 0.5 - Math.random()).slice(0, 10 - selectedWords.length)];
      }
      startGame(Array.from(new Map(selectedWords.map(w => [w.word, w])).values()), "meaning", "quick", "quick");
    } catch (err) {
      console.error("Error starting mixed practice:", err);
      setIsStarting(false);
    }
  };

  return {
    gameType, setGameType, words, currentIndex, currentWord, lives, score, feedback,
    options, selectedGroup, elapsedTime, speedyCount, selectedOption, selectedSet,
    setSelectedSet, playMode, setPlayMode, isPaused, isTrendOpen, setIsTrendOpen,
    gameLength, setGameLength, isGameOver, streakFeedback, setStreakFeedback,
    scoreHistoryMap, questionStartTime, setQuestionStartTime,
    dbHistory, isHistoryLoading, dbBestScore, isStarting, isShowingMeaning,
    correctOptionId, selectedPair, correctPairIds,
    // Actions
    startGame, nextWord, goHome, handleConnotation, handleOptionClick,
    handleVisualization, handleLearnContinue, handleSetupStart,
    handleStartReview, handleStartMixedPractice,
    generateOptions, generateImageOptions, generateToneClashOptions,
    showLivesOverPopup, confirmLivesOver,
    workoutReward,
    isSettingsOpen, handleOpenSettings, handleCloseSettings,
    showMeaningFeedback, toggleMeaningFeedback
  };
};
